import { SkeletonPlaceholder } from "@expert/common-ui";
import { type SkeletonProps, Text, type TextProps } from "@mantine/core";
import type { JSX } from "react";
import type { ChatTask } from "@/sdk";

type CustomerNameProps = Pick<ChatTask, "customerName"> & {
    size?: string;
    truncate?: TextProps["truncate"] | { maxChars?: number };
    lineClamp?: number;
};

export const CustomerName = ({
    customerName,
    size = "xs",
    truncate = "end",
    lineClamp,
    ...skeletonProps
}: CustomerNameProps & SkeletonProps): JSX.Element => {
    const displayName = customerName.error ? "Name unavailable" : customerName.value;

    // Handle the maxChars truncation if specified
    const hasMaxChars = typeof truncate === "object" && truncate !== null && "maxChars" in truncate;
    const maxChars = hasMaxChars ? truncate.maxChars : undefined;

    // Truncate text based on maxChars if specified
    let displayText = displayName;
    if (maxChars && displayName && displayName.length > maxChars) {
        displayText = `${displayName.substring(0, maxChars)}...`;
    }

    const textTruncate = hasMaxChars ? false : (truncate as TextProps["truncate"]);

    return (
        <SkeletonPlaceholder visible={customerName.loading} {...skeletonProps}>
            <Text
                span
                fw="bold"
                size={size}
                truncate={textTruncate}
                lineClamp={lineClamp}
                maw="100%"
                title={displayName} // Show full name on hover
            >
                {displayText}
            </Text>
        </SkeletonPlaceholder>
    );
};
